import { finalizeInfo } from '../buildTermSheet'
import { mergeDeep } from '../buildTermSheet'
import { deriveExitRoutes } from '../../vaults/exitRoutes'
import { resolveVaultProfileId } from '../profiles'
import { TERM_SHEET_SCHEMA_VERSION } from '../types'
import type {
  AvailabilityTerms,
  CounterpartyTerms,
  CoverageInfo,
  DeepPartial,
  ExposureTerms,
  FeeTerm,
  GovernanceTerms,
  MaturityTerms,
  OracleTerms,
  PositionConstraints,
  RateTerms,
  SupplyExitMode,
  SupplyExitRoute,
  SupplyExitTerms,
  SupplyTermSheet,
  TermSheet,
  UtilizationTerms,
} from '../types'
import type { VaultTermInput } from './input'
import { vaultTraits, windowFromAprWindow } from './providers'
import type { VaultProviderTraits } from './providers'

/**
 * The vault term-sheet builder — the borrow-less counterpart of
 * `buildTermSheet`.
 *
 * A separate builder rather than a widened one: the generic lending builder is
 * mostly liquidation, collateral factors, e-modes and rate menus, none of which
 * a vault has, and threading a `isVault` flag through all of it would leave
 * every branch guessing. What the two DO share is the part that matters for
 * consistency — `finalizeInfo`, `deriveSupplyTags` and `format.ts` — so a vault
 * sheet reads identically to a lending sheet in the same UI, tags included.
 *
 * The rule this file follows everywhere: **a block we cannot fill is absent and
 * declared in `coverage`, never defaulted to a comfortable value.** A `fees: []`
 * reads as "free", a `solvency: 'overcollateralized'` reads as a solvency
 * guarantee, and both would be assertions we have no basis for.
 */

const isNum = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v)

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

// ---------------------------------------------------------------------------
// rate
// ---------------------------------------------------------------------------

function buildRate(
  input: VaultTermInput,
  t: VaultProviderTraits,
  maturity: MaturityTerms,
): RateTerms {
  // `aprTotal` is DERIVED from the components, never taken from the provider's
  // own all-in field, because the schema's hardest invariant is that the two
  // reconcile (`validateTermSheet` → `apr-total`). Every provider documents its
  // `depositRate` as exactly `supplyRate + rewardsRate`, so this agrees with
  // them; where it would not, the components are what the sheet actually shows
  // and a headline contradicting its own breakdown is the worse failure.
  //
  // The provider's figure is used only when BOTH legs are missing — then it is
  // the base, not a third number floating above two zeros.
  const hasLegs = input.supplyRate != null || input.rewardsRate != null
  const base = hasLegs ? (input.supplyRate ?? 0) : (input.totalRate ?? 0)
  const rewards = input.rewardsRate ?? 0
  const total = base + rewards

  // A NAV-attested share price is not a governance-set rate: nobody sets it,
  // it is whatever an operator publishes. This is the distinction the `vault.nav`
  // profile exists to make, and it was unreachable before the reader kind was
  // surfaced onto the row.
  const isNav = input.navOracle != null || input.solvency === 'nav-attested'
  // A fixed term with no END is not a fixed term. The provider default says a
  // PT's rate is locked, but that claim is only true because a maturity backs
  // it — a row that lost its expiry must not keep the "Fixed 9 %" headline,
  // which would promise a rate for a period nobody can name.
  const termIsBacked = maturity.kind === 'fixed-date'
  const window = resolveRateWindow(input, t)
  // A number MEASURED over a past window is not a rate anyone sets, whatever
  // the provider's default says. Two things prove it: an explicitly trailing
  // window, and a `volatile` yield profile (a share price that can fall in
  // asset terms).
  //
  // This is not cosmetic. Yield Basis's yb-WBTC / yb-tBTC / yb-WETH are
  // `savings` rows printing NEGATIVE trailing returns, and Lagoon carries one at
  // −359 %; describing those as "set by the protocol and subject to change"
  // reads as a protocol that decided to charge you, rather than a strategy that
  // lost money. `realized` also stops them being sorted against live offers.
  const measured =
    window?.kind === 'trailing' || input.yieldProfile === 'volatile'
  const kind = isNav
    ? 'nav-accrual'
    : measured && t.rateKind !== 'fixed-term'
      ? 'realized'
      : t.rateKind === 'fixed-term' && !termIsBacked
        ? 'variable-managed'
        : t.rateKind
  const source = isNav ? 'oracle' : t.rateSource

  return {
    kind,
    apr: base,
    components: {
      base,
      rewards: rewards || undefined,
      // A vault has no separate intrinsic leg — whatever it pays IS the venue's
      // own yield. For a Pendle PT in particular the underlying's floating APY
      // is what the buyer GIVES UP, so writing it here would double-count the
      // two sides of one trade.
      intrinsic: undefined,
    },
    aprTotal: total,
    basis: 'apr-nominal',
    // `per-second` is right for a vault whose share price grows continuously,
    // which is nearly all of them — but not for a LINEAR accrual. Frankencoin's
    // savings module books `Δticks × saved / 1e6 / 365 days` and only compounds
    // if someone happens to call `refresh`, so claiming per-second here would
    // imply an APY ~2 % relative above what it actually pays. Providers say so
    // through `accrual`.
    compounding:
      kind === 'none'
        ? 'none'
        : input.accrual === 'linear'
          ? 'none'
          : 'per-second',
    // A warm-up is neither a withdrawal lock nor a governance notice period —
    // see `RateTerms.warmupSecs`. Absent ⇒ earns from the first block.
    warmupSecs:
      input.yieldWarmupSeconds && input.yieldWarmupSeconds > 0
        ? input.yieldWarmupSeconds
        : undefined,
    source,
    // A PT's discount is fixed at purchase; every other vault floats.
    isLocked: kind === 'fixed-term' && termIsBacked,
    window,
  }
}

function resolveRateWindow(
  input: VaultTermInput,
  t: VaultProviderTraits,
): RateTerms['window'] {
  // Yearn publishes a projection and a measurement under ONE field name; the
  // flag is the only thing that tells them apart.
  if (input.isForwardApr === true) return { kind: 'forward' }
  if (input.isForwardApr === false) return { kind: 'trailing' }
  const fromAprWindow = windowFromAprWindow(input.aprWindow)
  if (fromAprWindow) return fromAprWindow
  if (t.rateWindow) return t.rateWindow
  // A volatile share price cannot have a spot rate — whatever number reached
  // the row was measured after the fact, even on a provider whose other rows
  // are governance-set (Yield Basis lives inside `savings`).
  if (input.yieldProfile === 'volatile') return { kind: 'trailing' }
  return undefined
}

// ---------------------------------------------------------------------------
// maturity
// ---------------------------------------------------------------------------

function buildMaturity(input: VaultTermInput, now: number): MaturityTerms {
  const expiry = input.expiry
  if (!isNum(expiry) || expiry <= 0) return { kind: 'perpetual' }
  return {
    kind: 'fixed-date',
    maturity: expiry,
    maturityIso: new Date(expiry * 1000).toISOString(),
    // A SNAPSHOT — a cached sheet ages, so a countdown must be recomputed from
    // `maturity` rather than ticked down from this.
    secondsToMaturity: Math.max(0, expiry - now),
    // A PT redeems for the underlying at par and then sits there: no penalty,
    // no liquidation, no auto-roll, and no further yield.
    atMaturity: 'stops-earning',
  }
}

// ---------------------------------------------------------------------------
// fees
// ---------------------------------------------------------------------------

function buildFees(input: VaultTermInput, t: VaultProviderTraits): FeeTerm[] {
  const out: FeeTerm[] = []

  if (isNum(input.fee) && input.fee !== 0) {
    out.push({
      id: 'performance',
      label: 'Performance fee',
      when: 'performance',
      unit: 'percent',
      basis: 'yield',
      // Normalized to PERCENT here, because the providers disagree: `aave-earn`
      // publishes a fraction and the other five publish percent, so a uniform
      // read renders a 10 % curator cut as "0.1 %".
      value: t.feeIsFraction ? input.fee * 100 : input.fee,
      payee: 'curator',
      mutable: true,
      description:
        'Taken from the yield the vault earns, not from your principal. The published rate is already net of it.',
    })
  }

  // Charged on ASSETS, not on yield — so unlike the performance fee it is owed
  // in a flat year, and a sheet listing only the performance leg understates
  // the cost of holding. Upshift is the first provider to publish both.
  if (isNum(input.managementFee) && input.managementFee !== 0) {
    out.push({
      id: 'management',
      label: 'Management fee',
      when: 'ongoing',
      unit: 'apr-percent',
      basis: 'principal',
      value: t.feeIsFraction ? input.managementFee * 100 : input.managementFee,
      payee: 'curator',
      mutable: true,
      description:
        'Accrues on your whole balance for as long as you hold, whether or not the vault earns anything. The published rate is already net of it.',
    })
  }

  if (isNum(input.withdrawFeeBps) && input.withdrawFeeBps !== 0) {
    out.push({
      id: 'instant-exit',
      label: 'Instant-exit fee',
      when: 'exit',
      unit: 'bps',
      basis: 'shares',
      value: input.withdrawFeeBps,
      payee: 'protocol',
      description:
        'Deducted from the payout when you take the immediate exit. The queued exit pays at par and does not charge it.',
    })
  }

  // Pendle: the AMM fee is charged on BOTH legs, which no other provider does.
  // Two entries rather than one, because a client rendering "cost to exit"
  // would otherwise show half the round-trip.
  if (isNum(input.swapFeeRate) && input.swapFeeRate !== 0) {
    const pctValue = input.swapFeeRate * 100
    out.push(
      {
        id: 'origination',
        label: 'AMM swap fee (entry)',
        when: 'entry',
        unit: 'percent',
        basis: 'principal',
        value: pctValue,
        payee: 'lenders',
        indicative: true,
        description:
          'There is no deposit path — the position is BOUGHT on an AMM, and this fee is charged on that trade. Price impact is separate and depends on size.',
      },
      {
        id: 'settlement',
        label: 'AMM swap fee (exit)',
        when: 'exit',
        unit: 'percent',
        basis: 'principal',
        value: pctValue,
        payee: 'lenders',
        indicative: true,
        description:
          'Charged again when selling before maturity. Holding to maturity redeems at par and avoids it.',
      },
    )
  }

  // A signed gap between redemption value and fundamental value — negative
  // means redeeming BELOW fundamental. It is not a fee the protocol charges,
  // but it is money you do not receive, so it belongs on the same list.
  if (isNum(input.redemptionDiscountBps) && input.redemptionDiscountBps !== 0) {
    out.push({
      id: 'redemption',
      label: 'Redemption discount',
      when: 'exit',
      unit: 'bps',
      basis: 'shares',
      // Sign is load-bearing and preserved: this value goes POSITIVE when
      // redeeming above fundamental value, which is a real and common state.
      value: -input.redemptionDiscountBps,
      indicative: true,
      description:
        'The gap between what a share redeems for right now and its fundamental value. It moves with market conditions and is usually arbitraged back within hours.',
    })
  }

  void t
  return out
}

// ---------------------------------------------------------------------------
// exit
// ---------------------------------------------------------------------------

/**
 * The exit mode.
 *
 * The row's own `withdrawalMode` wins when it has one (savings and LST readers
 * establish it per vault, and it is authoritative). Otherwise the provider
 * default applies — and for the ERC-4626 vaults over lending markets that
 * default is `instant-capped`, NOT `instant`: those vaults hold a fraction of
 * TVL as cash, so a withdrawal above it cannot settle this block. `instant` is
 * restored only where the row PROVES full liquidity, because the claim is
 * load-bearing twice over — it is what a user reads as "my money is available",
 * and it is the gate on the illiquidity heuristic, which only fires on
 * same-block modes.
 */
function resolveExitMode(
  input: VaultTermInput,
  t: VaultProviderTraits,
): SupplyExitMode {
  const declared = input.withdrawalMode as SupplyExitMode | undefined
  if (declared) return declared
  if (t.defaultExitMode !== 'instant-capped') return t.defaultExitMode

  const { liquidity, totalAssets } = input
  // Unknown liquidity stays capped — the conservative answer is the true one
  // for every vault in this class, and "not published" is not "fully liquid".
  if (!isNum(liquidity) || !isNum(totalAssets)) return 'instant-capped'
  if (totalAssets <= 0) return 'instant'
  return liquidity >= totalAssets ? 'instant' : 'instant-capped'
}

/** Modes that describe a CHOICE of exits rather than one exit. */
const TWO_LEGGED_MODES: ReadonlySet<string> = new Set([
  'fee-or-queued',
  'instant-or-queued',
])

const ASYNC_MODES: ReadonlySet<string> = new Set([
  'fixed-cooldown',
  'queued',
  'request-based',
  'fee-or-queued',
  'instant-or-queued',
  'off-chain',
])

function settlementFor(
  mode: SupplyExitMode,
  priceRisk: SupplyExitTerms['priceRisk'],
): SupplyExitTerms['settlement'] {
  // An instrument with no redemption model at all: you leave by SELLING it.
  // Both `sync` and `async` mislead here — see `SupplyExitTerms.settlement`.
  if (
    mode === 'market-sale' ||
    mode === 'dex-only' ||
    priceRisk === 'market-price'
  )
    return 'market'
  return ASYNC_MODES.has(mode) ? 'async' : 'sync'
}

/**
 * Fallback route list for a row that predates `exitRoutes` (or an earn row
 * whose `providerMeta` does not carry it). Uses the resolved MODE rather than
 * the raw one, so an `instant-capped` vault derived from a liquidity reading
 * gets capped routes too. Empty stays empty — "we could not say" must not
 * render as "there is no way out".
 */
function derivedRoutes(
  input: VaultTermInput,
  mode: SupplyExitMode,
): SupplyExitRoute[] | undefined {
  const routes = deriveExitRoutes({
    withdrawalMode: mode,
    withdrawFeeBps: input.withdrawFeeBps,
    withdrawalCooldownSeconds: input.withdrawalCooldownSeconds,
    liquidity: isNum(input.liquidity) ? String(input.liquidity) : undefined,
    liquidityFormatted: input.liquidity,
    liquidityUsd: input.liquidityUsd,
  })
  return routes.length > 0 ? routes : undefined
}

function buildExit(
  input: VaultTermInput,
  t: VaultProviderTraits,
  fees: FeeTerm[],
): SupplyExitTerms {
  const mode = resolveExitMode(input, t)
  const priceRisk = resolvePriceRisk(input, t)

  const ratio =
    input.instantLiquidityRatio ??
    (isNum(input.liquidity) && isNum(input.totalAssets) && input.totalAssets > 0
      ? clamp01(input.liquidity / input.totalAssets)
      : undefined)

  return {
    mode,
    // The row's own list wins — providers that read their protocol's exit
    // modules attach live per-leg fees and minimums. Otherwise derive the
    // same split from the flat fields, because the alternative is a sheet
    // that states one wait and one fee belonging to DIFFERENT legs. Only a
    // row whose mode we cannot map at all ends up with no routes.
    routes: input.exitRoutes ?? derivedRoutes(input, mode),
    settlement: settlementFor(mode, priceRisk),
    cooldownSecs: input.withdrawalCooldownSeconds,
    liquidity:
      isNum(input.liquidity) || isNum(input.liquidityUsd)
        ? {
            assets: input.liquidity ?? 0,
            assetsUsd: input.liquidityUsd,
            ratio,
          }
        : undefined,
    // Every vault surface we serve lets a holder exit part of a position; the
    // constraint is always size or timing, never granularity.
    partialAllowed: true,
    priceRisk,
    // Only the request/queue models have something to cancel, and only where
    // the protocol exposes it — left undefined rather than guessed.
    fees: fees.filter((f) => f.when === 'exit' || f.when === 'performance'),
  }
}

function resolvePriceRisk(
  input: VaultTermInput,
  t: VaultProviderTraits,
): SupplyExitTerms['priceRisk'] {
  if (t.priceRisk !== 'none') return t.priceRisk
  // A vault publishing a signed gap between redemption and fundamental value
  // has a deterministic, readable haircut — a different thing from selling into
  // a book, and worth saying so rather than reporting par.
  if (isNum(input.redemptionDiscountBps) && input.redemptionDiscountBps !== 0)
    return 'haircut-formula'
  return 'none'
}

// ---------------------------------------------------------------------------
// availability
// ---------------------------------------------------------------------------

function buildAvailability(
  input: VaultTermInput,
  now: number,
): AvailabilityTerms {
  const capacity = input.depositCapacity
  const capFull = capacity === '0'
  const paused = input.paused === true || input.depositsPaused === true
  const gated = input.isMintable === false
  const closed = input.isClosed === true
  // Judged against the INJECTED clock, not `Date.now()` — the sheet's own
  // `asOf` is what every other field is stated as of, and a maturity check
  // reading a different clock would let one sheet contradict itself.
  const matured =
    isNum(input.expiry) && input.expiry > 0 ? input.expiry <= now : false

  const canOpen = !paused && !gated && !capFull && !closed && !matured

  let blockedBy: AvailabilityTerms['blockedBy']
  if (!canOpen) {
    // Order matters: a matured product is gone regardless of what else is true,
    // and a stale row still carries its last pre-expiry rate.
    if (matured) blockedBy = 'shutdown'
    else if (paused) blockedBy = 'paused'
    else if (gated) blockedBy = 'not-whitelisted'
    else if (capFull) blockedBy = 'cap-full'
    else if (closed) blockedBy = 'disabled'
  }

  const cap = capacity ?? input.supplyCap

  return {
    canOpen,
    // Exits stay open under a cap, a mint allowlist or a maturity — every
    // wind-down we serve keeps the door open outward, and a matured bond is
    // exactly the case where the holder still needs out. An explicit
    // withdrawal pause is the one thing that shuts it.
    canClose: input.withdrawalsPaused !== true && input.paused !== true,
    blockedBy,
    gating: gated ? 'whitelist' : 'permissionless',
    cap: cap != null && cap !== '0' ? cap : undefined,
    // Almost every vault pulls the deposit with `transferFrom` and therefore
    // needs an allowance — but not all. Frankencoin's savings modules are
    // registered ZCHF minters, and the token grants a registered minter an
    // implicit infinite allowance, so their deposit route emits NO approval.
    // Asserting one here would make the sheet contradict the envelope built
    // beside it, which is worse than saying nothing.
    requires: input.needsDepositApproval === false ? [] : ['token-approval'],
    opensWith: 'standalone',
  }
}

// ---------------------------------------------------------------------------
// backing / counterparty / principal
// ---------------------------------------------------------------------------

function buildBacking(input: VaultTermInput): ExposureTerms | undefined {
  const ex = input.exposures
  if (!ex?.length) return undefined
  const items = ex.map((e) => ({
    asset: {
      chainId: input.chainId,
      address: (e.collateralAddress ?? '').toLowerCase(),
      symbol: e.collateral?.symbol,
      decimals: e.collateral?.decimals,
      logoURI: e.collateral?.logoURI,
    },
    marketUid: e.marketUid,
    // The tagged idle slice is not an exposure to anything — it is cash.
    via: (e.idle ? 'idle' : 'vault-allocation') as 'idle' | 'vault-allocation',
    assets: e.assets,
    assetsUsd: e.assetsUsd,
    weightPct: e.weightPct,
  }))
  const weights = items
    .map((i) => i.weightPct)
    .filter((w): w is number => isNum(w))
  return {
    count: items.length,
    // A real measured split, unlike the pooled-lender case.
    weightBasis: 'allocation',
    topWeightPct: weights.length ? Math.max(...weights) : undefined,
    items,
  }
}

function buildCounterparty(
  input: VaultTermInput,
  t: VaultProviderTraits,
): CounterpartyTerms {
  return {
    kind: t.counterpartyKind,
    address: input.address,
    // Three tiers, most specific first: a CURATED per-vault classification, then
    // the provider's STRUCTURAL answer where one exists, then an assumption.
    // `coverage` records which tier answered, so a consumer can always tell an
    // asserted `overcollateralized` from an assumed one.
    solvency: input.solvency ?? t.solvency ?? 'overcollateralized',
    socializedLoss: input.solvency === 'tranched-junior' ? true : undefined,
    curator: input.curatorName ?? input.curator,
  }
}

function buildPrincipal(
  input: VaultTermInput,
  t: VaultProviderTraits,
): SupplyTermSheet['principal'] {
  const risks: SupplyTermSheet['principal']['risks'] = []
  if (t.volatilePrincipal || input.yieldProfile === 'volatile')
    risks.push('nav-drawdown')
  if (input.solvency === 'tranched-junior') risks.push('first-loss')
  if (input.solvency === 'undercollateralized') risks.push('bad-debt')
  if (input.solvency === 'nav-attested' || input.navOracle != null)
    risks.push('nav-drawdown')
  return {
    protected: risks.length === 0,
    risks: [...new Set(risks)],
  }
}

// ---------------------------------------------------------------------------
// market-level blocks
// ---------------------------------------------------------------------------

function buildGovernance(
  input: VaultTermInput,
  t: VaultProviderTraits,
): GovernanceTerms | undefined {
  if (!t.reportsGovernance) return undefined
  const allocators =
    input.allocators && input.allocators.length > 0
      ? input.allocators
      : undefined
  const roles = {
    owner: input.owner,
    curator: input.curator,
    guardian: input.guardian,
    feeRecipient: input.feeRecipient,
    allocators,
  }
  const hasRole = Object.values(roles).some((r) => r != null)
  if (!isNum(input.timelock) && !hasRole) return undefined
  return {
    mutability: 'governed',
    controller: input.owner ?? input.curator,
    // The curator is an address with powers, not a verified timelock — claiming
    // a controller KIND we did not screen would be the one field here that
    // reads as a safety assurance.
    controllerKind: 'UNKNOWN',
    // `0` is REAL on the providers that run no timelock pattern (Lista earn) —
    // it means no notice period, which is exactly what a holder needs told.
    timelockSecs: input.timelock,
    // Read off the VAULT, not inferred from a governance screen — and the vault
    // enforces the queue itself, which is why this delay stands even though the
    // controller above is an unscreened curator address.
    timelockSource: 'on-chain',
    // `reallocate` is listed only where an allocator actually exists, and it
    // is the power the timelock above does NOT cover: reshuffling between
    // already-approved markets lands in the next block. Stating it is what
    // stops `timelockSecs: 86400` from reading as "nothing about this vault
    // can change today".
    powers: allocators
      ? ['set-caps', 'change-fees', 'pause-withdrawals', 'reallocate']
      : ['set-caps', 'change-fees', 'pause-withdrawals'],
    roles: hasRole ? roles : undefined,
  }
}

function buildOracle(input: VaultTermInput): OracleTerms | undefined {
  if (input.navOracle == null && input.solvency !== 'nav-attested')
    return undefined
  return {
    kind: 'nav-attested',
    address: input.navOracle?.toLowerCase(),
    priceDescription: 'Net asset value published by the operator',
  }
}

function buildUtilization(
  input: VaultTermInput,
  t: VaultProviderTraits,
): UtilizationTerms | undefined {
  if (!t.hasUtilization) return undefined
  const borrowed = input.totalBorrowed
  const total = input.expectedLiquidity
  if (!isNum(borrowed) || !isNum(total) || total <= 0) return undefined
  return {
    utilization: clamp01(borrowed / total),
    // The ratio is the underlying pool's, not this share token's.
    basis: 'pool',
    irmTotalDeposits: total,
    irmTotalDebt: borrowed,
  }
}

function buildConstraints(): PositionConstraints {
  return {
    // Vault shares are fungible ERC-20s held by an address — there is no
    // sub-account, no id and nothing to discover.
    crossMargin: false,
    positionModel: 'account',
    positionIdMeaning:
      'A vault position is just a share-token balance; there is no position id.',
  }
}

// ---------------------------------------------------------------------------
// coverage
// ---------------------------------------------------------------------------

/**
 * What was computed, what does not apply, and what is simply not wired.
 *
 * This block is what lets the vault half ship honestly while a third of the
 * inputs are still missing: an absent `governance` on a savings vault means
 * "no registry column exists yet", while an absent `backedBy` on an LST means
 * "there is nothing to decompose". Collapsing those two into one silence is
 * what would make the sheet untrustworthy.
 */
function buildCoverage(
  sheet: TermSheet,
  input: VaultTermInput,
  t: VaultProviderTraits,
): CoverageInfo {
  const present: string[] = ['supply']
  const notApplicable: Record<string, string> = {
    borrow: 'vaults-have-no-borrow-side',
  }
  const pending: Record<string, string> = {}

  if (sheet.utilization) present.push('utilization')
  else if (!t.hasUtilization)
    notApplicable.utilization = 'no-debt-accumulator-on-this-position'
  else pending.utilization = 'underlying-market-totals-not-joined'

  if (sheet.governance) present.push('governance')
  else if (t.reportsGovernance)
    pending.governance = 'provider-reports-governance-but-this-row-carried-none'
  else pending.governance = 'no-governance-column-for-this-provider'

  if (sheet.oracle) present.push('oracle')
  else notApplicable.oracle = 'share-price-is-computed-not-quoted'

  if (sheet.supply?.backedBy) present.push('backedBy')
  else if (!t.hasDecomposableBacking)
    notApplicable.backedBy = 'position-has-no-decomposable-backing'
  else pending.backedBy = 'provider-does-not-publish-exposures'

  if (sheet.supply?.fees.length) present.push('fees')
  else if (!t.reportsPerformanceFee)
    // An empty fee list reads as "free". If the provider publishes no fee at
    // all we must say we do not know, not that there is nothing to pay.
    pending.fees = 'provider-publishes-no-fee-schedule'

  if (input.solvency) present.push('counterparty')
  else if (t.solvency) present.push('counterparty')
  else pending.counterparty = 'solvency-not-curated-default-assumed'

  if (sheet.supply?.exit.liquidity) present.push('exitLiquidity')
  else pending.exitLiquidity = 'provider-does-not-publish-liquidity'

  // The per-leg split is the part a two-legged mode cannot be honest without.
  // `notApplicable` rather than `pending` for single-leg modes: nothing is
  // missing there — the one route IS the exit.
  if (sheet.supply?.exit.routes?.length) present.push('exitRoutes')
  else if (TWO_LEGGED_MODES.has(String(sheet.supply?.exit.mode)))
    pending.exitRoutes = 'provider-does-not-publish-per-leg-terms'
  else notApplicable.exitRoutes = 'single-exit-route'

  return {
    present,
    notApplicable: Object.keys(notApplicable).length
      ? notApplicable
      : undefined,
    pending: Object.keys(pending).length ? pending : undefined,
  }
}

// ---------------------------------------------------------------------------
// entry point
// ---------------------------------------------------------------------------

export interface BuildVaultTermSheetOptions {
  /** Unix seconds; injected so tests are deterministic. */
  now?: number
  /** Merged over the generic result, same contract as a lender adapter. */
  patch?: DeepPartial<TermSheet>
}

/** Build one complete term sheet for one vault row. */
export function buildVaultTermSheet(
  input: VaultTermInput,
  opts: BuildVaultTermSheetOptions = {},
): TermSheet {
  const now = opts.now ?? Math.floor(Date.now() / 1000)
  const t = vaultTraits(input.provider) ?? FALLBACK_TRAITS

  const maturity = buildMaturity(input, now)
  const fees = buildFees(input, t)
  const supply: SupplyTermSheet = {
    // A vault share is never collateral IN the vault — it earns, full stop.
    // (It may be collateral somewhere else; that is the other market's sheet.)
    role: 'yield',
    rate: buildRate(input, t, maturity),
    maturity,
    exit: buildExit(input, t, fees),
    fees,
    backedBy: buildBacking(input),
    counterparty: buildCounterparty(input, t),
    availability: buildAvailability(input, now),
    principal: buildPrincipal(input, t),
    info: {
      headline: '',
      // The curated per-vault explainer wins over the templated one where it
      // exists — ~90 savings rows already carry hand-written copy that says
      // more than any template can.
      description: input.description ?? '',
      tags: [],
    },
  }

  const base: TermSheet = {
    schemaVersion: TERM_SHEET_SCHEMA_VERSION,
    asOf: now,
    profileId: resolveVaultProfileId(input.provider, input.solvency),
    marketUid: input.vaultUid,
    lender: `vault.${input.provider}`,
    chainId: input.chainId,
    asset: input.asset,
    supply,
    borrow: undefined,
    governance: buildGovernance(input, t),
    oracle: buildOracle(input),
    utilization: buildUtilization(input, t),
    constraints: buildConstraints(),
  }

  const merged = mergeDeep(base, opts.patch)
  // Same guard as the lending builder: a patch may refine the supply side but
  // must never conjure a borrow side onto a vault.
  merged.borrow = undefined

  const withInfo = finalizeInfo(merged)
  withInfo.coverage = buildCoverage(withInfo, input, t)
  return withInfo
}

/**
 * Used only when a row arrives with a provider this build does not know —
 * a recorder running ahead of the SDK. Deliberately the most conservative
 * combination in the table rather than the most common one.
 */
const FALLBACK_TRAITS: VaultProviderTraits = {
  rateKind: 'variable-managed',
  rateSource: 'api',
  defaultExitMode: 'instant-capped',
  priceRisk: 'none',
  counterpartyKind: 'vault-strategy',
  reportsPerformanceFee: false,
  reportsGovernance: false,
  reportsExposures: false,
  hasDecomposableBacking: false,
  hasUtilization: false,
}
